// Order 66 "Genesys Vehicle Rules in Star Wars" conversion.

export const GENESYS_VEHICLES_PACK_ID = "genesysVehicles";

export const GENESYS_OPTIONS_STORAGE_KEY = "genesysVehicleOptions";

export const GENESYS_VEHICLE_OPTIONS = [
    { id: "speed", label: "Max Speed cap", description: "Rename Speed to Max Speed and cap it at 5." },
    { id: "defense", label: "Single Defense", description: "Collapse the defensive zones into one averaged Defense value." },
    { id: "armor", label: "Armor halved", description: "Halve Armor, rounding up (minimum 1)." },
    { id: "hullTrauma", label: "Hull Trauma +silhouette", description: "Increase the Hull Trauma threshold by the vehicle's silhouette." },
    { id: "sensors", label: "Sensors → Strategic", description: "Set sensor range to Strategic." },
    { id: "weaponDamage", label: "Weapon damage −2", description: "Reduce every vehicle weapon's damage by 2." },
    { id: "weaponQualities", label: "Blast/Breach −2, Guided +1", description: "Adjust the Blast, Breach and Guided quality values." },
    { id: "weaponRange", label: "Weapon range conversion", description: "Apply the range-band changes." },
];

export function genesysVehiclesEnabled() {
    try {
        const state = JSON.parse(localStorage.getItem("homebrewState") || "{}");
        return !!state[GENESYS_VEHICLES_PACK_ID];
    } catch (e) {
        return false;
    }
}

export function getGenesysOptions() {
    let stored = {};
    try {
        stored = JSON.parse(localStorage.getItem(GENESYS_OPTIONS_STORAGE_KEY) || "{}");
    } catch (e) {
        stored = {};
    }
    const opts = {};
    for (const option of GENESYS_VEHICLE_OPTIONS) {
        opts[option.id] = stored[option.id] !== false;
    }
    return opts;
}

export function setGenesysOption(id, value) {
    let stored = {};
    try {
        stored = JSON.parse(localStorage.getItem(GENESYS_OPTIONS_STORAGE_KEY) || "{}");
    } catch (e) {
        stored = {};
    }
    stored[id] = value;
    localStorage.setItem(GENESYS_OPTIONS_STORAGE_KEY, JSON.stringify(stored));
}

const int = (v, fallback = 0) => {
    const n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
};

const hasValue = (v) => v !== undefined && v !== null && v !== "";

const rangeByKeyword = (name) => {
    const l = (name || "").toLowerCase();
    const word = (w) => new RegExp(`\\b${w}\\b`).test(l);

    if (word("superlaser")) return "Strategic";
    if (word("turbolaser")) return l.includes("light") ? "Extreme" : "Strategic";
    if (word("torpedo") || word("missile")) return "Extreme";
    if (word("ion") && (l.includes("cannon") || l.includes("blaster"))) {
        return l.includes("light") ? "Long" : "Extreme";
    }
    if (word("laser")) return l.includes("heavy") ? "Extreme" : "Long";

    // Judgement calls not explicit in O66 guide:
    // direct-fire cannons match lasers (Long)
    // rockets/bombs/grenades/warheads, mines, and flak are Medium;
    // kinetic mass drivers are Extreme
    if (word("flak")) return "Medium";
    if (word("blaster") || word("suppression") || l.includes("beam cannon")) return "Long";
    if (word("rocket") || word("bomb") || word("grenade") || word("warhead") || word("flak") || word("ordnance") || word("ordinance") || word("mine")) return "Medium";
    if (l.includes("mass driver") || l.includes("mass-driver")) return "Extreme";
    return null;
};

export function convertVehicle(vehicle) {
    if (!vehicle || !genesysVehiclesEnabled()) return vehicle;

    const opts = getGenesysOptions();
    const sil = int(vehicle.Silhouette);
    const v = { ...vehicle };

    if (opts.speed && hasValue(vehicle.Speed)) {
        v.Speed = Math.min(int(vehicle.Speed), 5).toString();
        v.GenesysMaxSpeed = true;
    }

    if (opts.defense) {
        const zoneCount = sil >= 5 ? 4 : 2;
        const zoneTotal =
            int(vehicle.DefFore) +
            int(vehicle.DefAft) +
            int(vehicle.DefPort) +
            int(vehicle.DefStarboard);
        v.GenesysDefense = Math.round(zoneTotal / zoneCount);
        v.GenesysDefenseApplied = true;
    }

    if (opts.armor && hasValue(vehicle.Armor)) {
        v.Armor = Math.max(1, Math.ceil(int(vehicle.Armor) / 2)).toString();
    }

    if (opts.hullTrauma && hasValue(vehicle.HullTrauma)) {
        v.HullTrauma = (int(vehicle.HullTrauma) + sil).toString();
    }
    if (opts.sensors) {
        v.SensorRange = "Strategic";
    }

    v.GenesysConverted = true;
    return v;
}

export function convertVehicleWeapon(weapon) {
    if (!weapon || !genesysVehiclesEnabled()) return weapon;

    const opts = getGenesysOptions();
    const w = { ...weapon };

    if (opts.weaponDamage && hasValue(weapon.Damage) && !isNaN(parseInt(weapon.Damage, 10))) {
        w.Damage = Math.max(0, int(weapon.Damage) - 2).toString();
    }

    if (opts.weaponRange) {
        let range = rangeByKeyword(weapon.Name);
        if (range === null) range = weapon.Range;
        if (range === "Close") range = "Engaged";
        if (hasValue(range)) w.Range = range;
    }

    if (opts.weaponQualities && Array.isArray(weapon.Qualities)) {
        w.Qualities = weapon.Qualities
            .map((q) => {
                if (q.Key === "BLAST" || q.Key === "BREACH") {
                    const value = int(q.Count) - 2;
                    return value > 0 ? { ...q, Count: value.toString() } : null;
                }
                // Guided value is increased by 1.
                if (q.Key === "GUIDED") {
                    return { ...q, Count: (int(q.Count) + 1).toString() };
                }
                return q;
            })
            .filter(Boolean);
    }

    return w;
}
